import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";

const EXTRA_EMAIL_FIELDS = ["email2", "email3", "email4", "email5"];

async function ensurePdfBlob(input) {
  if (!input) return null;

  // Already a Blob/File
  if (input instanceof Blob) return input;

  // If you passed a promise that resolves to a Blob
  if (typeof input?.then === "function") {
    const resolved = await input;
    if (resolved instanceof Blob) return resolved;
  }

  // If it's a fetch Response
  if (input instanceof Response) {
    return await input.blob();
  }

  // If it's a base64 data URL string
  if (typeof input === "string" && input.startsWith("data:application/pdf")) {
    const res = await fetch(input);
    return await res.blob();
  }

  // If it's raw base64 string (no data: prefix)
  if (typeof input === "string" && /^[A-Za-z0-9+/=\s]+$/.test(input)) {
    const byteChars = atob(input.replace(/\s/g, ""));
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    return new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
  }

  console.warn("pdfBlob is not a Blob/File:", input);
  return null;
}


function dataUrlToBlob(dataUrl) {
  const [meta, base64] = dataUrl.split(",");
  const mime =
    meta?.match(/data:(.*?);base64/)?.[1] || "application/pdf";

  const bin = atob(base64 || "");
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

  return new Blob([bytes], { type: mime });
}

function normalizePdfBlob(maybeBlob) {
  if (!maybeBlob) return null;

  // already a Blob/File
  if (maybeBlob instanceof Blob) return maybeBlob;

  // sometimes wrapped like { blob: Blob }
  if (maybeBlob?.blob instanceof Blob) return maybeBlob.blob;

  // sometimes a base64/dataURL string
  if (typeof maybeBlob === "string" && maybeBlob.startsWith("data:")) {
    return dataUrlToBlob(maybeBlob);
  }

  // anything else is invalid
  return null;
}


function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function EmailStatementModal({
  show,
  onHide,
  defaultEmail,
  email2,
  email3,
  email4,
  email5,
  statementData,
  pdfBlob,
  sendStatementByEmail,
}) {
  // Primary always exists (can be blank if no default)
  const [recipientEmail, setRecipientEmail] = useState("");

  // Extra fields: undefined = hidden, "" or "value" = visible
  const [extraEmails, setExtraEmails] = useState({
    email2: undefined,
    email3: undefined,
    email4: undefined,
    email5: undefined,
  });

  const [statementPassword, setStatementPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const defaultsMap = useMemo(
    () => ({
      email2: (email2 || "").trim(),
      email3: (email3 || "").trim(),
      email4: (email4 || "").trim(),
      email5: (email5 || "").trim(),
    }),
    [email2, email3, email4, email5]
  );

  // On open: show all fields that already have values
  useEffect(() => {
    if (!show) return;

    setErrorMessage("");
    setRecipientEmail((defaultEmail || "").trim());

    setExtraEmails({
      email2: defaultsMap.email2 ? defaultsMap.email2 : undefined,
      email3: defaultsMap.email3 ? defaultsMap.email3 : undefined,
      email4: defaultsMap.email4 ? defaultsMap.email4 : undefined,
      email5: defaultsMap.email5 ? defaultsMap.email5 : undefined,
    });
  }, [show, defaultEmail, defaultsMap]);

  const handleAddExtraField = () => {
    const nextField = EXTRA_EMAIL_FIELDS.find((f) => extraEmails[f] === undefined);
    if (!nextField) return;

    // Show it as blank input (not saved to account; only for sending)
    setExtraEmails((prev) => ({ ...prev, [nextField]: "" }));
  };

  const handleRemoveExtraField = (field) => {
    setExtraEmails((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleExtraChange = (field, value) => {
    setExtraEmails((prev) => ({ ...prev, [field]: value }));
  };

  const allExtraUsed = EXTRA_EMAIL_FIELDS.every((f) => extraEmails[f] !== undefined);

const handleSendEmail = async () => {
  setErrorMessage("");

  const emails = [
    (recipientEmail || "").trim(),
    ...EXTRA_EMAIL_FIELDS.map((f) => (extraEmails[f] || "").trim()),
  ].filter(Boolean);

  if (!emails.length) {
    setErrorMessage("At least one email address is required.");
    return;
  }

  const invalid = emails.filter((e) => !isValidEmail(e));
  if (invalid.length) {
    setErrorMessage(`These email(s) look invalid: ${invalid.join(", ")}`);
    return;
  }

  // ✅ ensure real Blob
  const pdf = await ensurePdfBlob(pdfBlob);
  if (!pdf) {
    setErrorMessage("Statement PDF is not ready yet. Please generate the PDF first.");
    return;
  }

  onHide(); // close immediately

  const formData = new FormData();
  formData.append("email", emails[0]);               // ✅ primary
  formData.append("email2", emails[1] || "");        // ✅ optional
  formData.append("email3", emails[2] || "");
  formData.append("email4", emails[3] || "");
  formData.append("email5", emails[4] || "");
  formData.append("statementData", JSON.stringify(statementData));
  formData.append("pdf", pdf, "Statement.pdf");

  sendStatementByEmail(formData).catch((err) => {
    console.error("Failed to send statement email:", err);
  });
  console.log("Statement email sent to:", emails);
};


  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Send Statement</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}

        <Form>
          {/* Primary Email (always visible) */}
          <Form.Group controlId="formGridRecipientEmail">
            <Form.Label>Email</Form.Label>
            <InputGroup className="align-items-center">
              <Form.Control
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Recipient email"
              />

              <Button
                variant="link"
                className="text-primary d-flex align-items-center"
                onClick={handleAddExtraField}
                disabled={allExtraUsed}
                title={allExtraUsed ? "Maximum emails added" : "Add another email"}
              >
                <FaPlus />
              </Button>
            </InputGroup>
          </Form.Group>

          {/* Additional Emails (visible if they had values OR user added via +) */}
          {EXTRA_EMAIL_FIELDS.map((field, idx) => {
            if (extraEmails[field] === undefined) return null;

            return (
              <Form.Group key={field} className="mt-2">
                <InputGroup className="align-items-center">
                  <Form.Control
                    type="email"
                    value={extraEmails[field]}
                    placeholder={`Additional Email ${idx + 2}`}
                    onChange={(e) => handleExtraChange(field, e.target.value)}
                  />
                  <Button
                    variant="link"
                    className="text-danger ms-0 d-flex align-items-center"
                    onClick={() => handleRemoveExtraField(field)}
                    title="Remove"
                  >
                    <FaTrash />
                  </Button>
                </InputGroup>
              </Form.Group>
            );
          })}

          <p className="mt-3 mb-0">
            The statement will be sent as a PDF attachment to all email
            addresses entered above. Any emails added here are only used for
            sending and won’t be saved on the account.
          </p>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" className="rounded-0" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" className="rounded-0" onClick={handleSendEmail}>
          Send
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
